import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createUser,
  getAllUsers,
  getUserById,
  updateUserById,
  toggleUserActive,
  User,
} from "./userAction";

interface UserState {
  users: User[];
  selectedUser: User | null;
  totalUsers: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  selectedUser: null,
  totalUsers: 0,
  currentPage: 1,
  totalPages: 1,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUserMessages(state) {
      state.error = null;
    },
    setSelectedUser(state, action: PayloadAction<User | null>) {
      state.selectedUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE USER
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        const user = action.payload.data?.user;
        if (user) {
          state.users.push(user);
        }
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "User creation failed";
      })

      // GET ALL USERS
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data?.users || [];
        state.totalUsers = action.payload.data?.total || 0;
        state.currentPage = action.payload.data?.page || 1;
        state.totalPages = action.payload.data?.pages || 1;
        state.error = null;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve users";
      })

      // GET USER BY ID
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.data?.user || null;
        state.error = null;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve user";
      })

      // UPDATE USER
      .addCase(updateUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserById.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUser = action.payload.data?.user;
        if (updatedUser) {
          state.users = state.users.map((user) =>
            user.id === updatedUser.id ? updatedUser : user
          );
          if (state.selectedUser?.id === updatedUser.id) {
            state.selectedUser = updatedUser;
          }
        }
        state.error = null;
      })
      .addCase(updateUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "User update failed";
      })

      // TOGGLE USER ACTIVE STATUS
      .addCase(toggleUserActive.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserActive.fulfilled, (state, action) => {
        state.loading = false;
        const toggledUser = action.payload.data?.user;
        if (toggledUser) {
          state.users = state.users.map((user) =>
            user.id === toggledUser.id ? toggledUser : user
          );
          if (state.selectedUser?.id === toggledUser.id) {
            state.selectedUser = toggledUser;
          }
        }
        state.error = null;
      })
      .addCase(toggleUserActive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "User status toggle failed";
      });
  },
});

export const { clearUserMessages, setSelectedUser } = userSlice.actions;
export default userSlice.reducer;
